// MF-67 — Free map pin picker (Leaflet + OpenStreetMap, no API key). Click or drag to drop the pin;
// returns { lat, lng }. Used by the entry location field and the zone form. Reusable and provider-free
// — if we ever move to Google/MapTiler tiles, only the TileLayer here changes. Has an "Expand" button
// to open the map big in a dialog (easier to pan/zoom to the exact spot).
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet'
import { Dialog, IconButton, Tooltip } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Vite serves the marker images as URLs; wire them so the default Leaflet pin renders (otherwise it
// 404s the images and shows nothing).
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

// Melbourne, VIC — sensible default when the school has no geo set yet.
const DEFAULT_CENTER = [-37.8136, 144.9631]

const ClickToPlace = ({ onChange }) => {
  useMapEvents({
    click: (e) => onChange({ lat: e.latlng.lat, lng: e.latlng.lng })
  })
  return null
}

// Pans/zooms the map when `to` changes (e.g. after geocoding an address). MapContainer's `center` is
// only the initial view, so we do it imperatively.
const Recenter = ({ to, zoom }) => {
  const map = useMap()
  useEffect(() => {
    if (to?.lat != null && to?.lng != null) map.setView([to.lat, to.lng], zoom || map.getZoom())
  }, [to?.lat, to?.lng, zoom, map])
  return null
}

// The actual map + layers, reused inline and inside the expand dialog.
const MapBody = ({ value, onChange, initialCenter, recenterTo, zoom }) => {
  const hasPin = value?.lat != null && value?.lng != null
  return (
    <MapContainer
      center={initialCenter}
      zoom={zoom}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom
    >
      <TileLayer
        attribution='&copy; OpenStreetMap contributors'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      <ClickToPlace onChange={onChange} />
      <Recenter to={recenterTo} zoom={zoom} />
      {hasPin ? (
        <Marker
          position={[value.lat, value.lng]}
          draggable
          eventHandlers={{
            dragend: (e) => {
              const { lat, lng } = e.target.getLatLng()
              onChange({ lat, lng })
            }
          }}
        />
      ) : null}
    </MapContainer>
  )
}

const MapPicker = ({ value = {}, onChange, center, recenterTo, zoom = 16, height = 300 }) => {
  const [expanded, setExpanded] = useState(false)
  const hasPin = value?.lat != null && value?.lng != null
  const initialCenter = hasPin
    ? [value.lat, value.lng]
    : center?.lat != null && center?.lng != null
      ? [center.lat, center.lng]
      : DEFAULT_CENTER

  return (
    <>
      <div
        style={{ height, width: '100%', position: 'relative' }}
        className='rounded overflow-hidden border border-muted'
      >
        <Tooltip title='Expand map' arrow>
          <IconButton
            size='small'
            onClick={() => setExpanded(true)}
            className='bg-background'
            style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}
          >
            <FontAwesomeIcon icon={['fas', 'expand-alt']} />
          </IconButton>
        </Tooltip>
        <MapBody
          value={value}
          onChange={onChange}
          initialCenter={initialCenter}
          recenterTo={recenterTo}
          zoom={zoom}
        />
      </div>

      <Dialog open={expanded} onClose={() => setExpanded(false)} maxWidth='lg' fullWidth>
        <div style={{ height: '75vh', width: '100%', position: 'relative' }}>
          <Tooltip title='Close' arrow>
            <IconButton
              size='small'
              onClick={() => setExpanded(false)}
              className='bg-background'
              style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}
            >
              <FontAwesomeIcon icon={['fal', 'times']} />
            </IconButton>
          </Tooltip>
          {expanded ? (
            <MapBody
              value={value}
              onChange={onChange}
              initialCenter={initialCenter}
              recenterTo={recenterTo}
              zoom={zoom}
            />
          ) : null}
        </div>
      </Dialog>
    </>
  )
}

export default MapPicker
